import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScienceIcon from '@mui/icons-material/Science';
import BotResponseRenderer from '../../components/mainchat/BotResponseRenderer';
import { classifyIntentDryRun } from '../../lib/api/intentTestApi';

const seedCases = [
  'ABC전자 견적서 만들어줘',
  '이번주 마감 알려줘',
  'BI_PJ_2025_04_001',
  '그거 어떻게 해',
  '견적서 만들어',
  '삼성SDS 프로젝트 찾아줘',
  '안녕',
  '제조 작업 완료',
];

const toRecentMessages = (value) => value
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean);

const prettyJson = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value, null, 2);
};

const IntentBotTestPage = () => {
  const [message, setMessage] = useState(seedCases[0]);
  const [currentPrjUuid, setCurrentPrjUuid] = useState('');
  const [recentMessages, setRecentMessages] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const botPreviewMessage = useMemo(() => {
    if (!result) {
      return {
        responseType: 'SIMPLE',
        message: 'dry-run 결과가 여기에 표시됩니다.',
      };
    }

    return {
      responseType: 'SIMPLE',
      message: [
        `intent: ${result.intent}`,
        `confidence: ${result.confidence}`,
        `dryRunToken: ${result.dryRunToken || '-'}`,
        `fastPathMatched: ${result.fastPathMatched ? 'true' : 'false'}`,
      ].join('\n'),
    };
  }, [result]);

  const runDryRun = async (nextMessage = message) => {
    const trimmedMessage = nextMessage.trim();
    if (!trimmedMessage) {
      setError('메시지를 입력하세요.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await classifyIntentDryRun({
        message: trimmedMessage,
        context: {
          currentPrjUuid: currentPrjUuid.trim() || null,
          recentMessages: toRecentMessages(recentMessages),
        },
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || '요청에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedClick = (seed) => {
    setMessage(seed);
    runDryRun(seed);
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f7fb', minHeight: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <ScienceIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Intent Bot Dry-run
        </Typography>
        <Chip size="small" label="Phase 0" color="primary" variant="outlined" />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                입력
              </Typography>
              <TextField
                label="message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                multiline
                minRows={5}
                fullWidth
              />
              <TextField
                label="currentPrjUuid"
                value={currentPrjUuid}
                onChange={(event) => setCurrentPrjUuid(event.target.value)}
                fullWidth
              />
              <TextField
                label="recentMessages"
                value={recentMessages}
                onChange={(event) => setRecentMessages(event.target.value)}
                multiline
                minRows={4}
                fullWidth
              />
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <PlayArrowIcon />}
                onClick={() => runDryRun()}
                disabled={loading}
                sx={{ alignSelf: 'flex-start' }}
              >
                실행
              </Button>

              <Divider />

              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                {seedCases.map((seed) => (
                  <Button
                    key={seed}
                    size="small"
                    variant="outlined"
                    onClick={() => handleSeedClick(seed)}
                    disabled={loading}
                  >
                    {seed}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minHeight: 280 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              봇 응답 카드
            </Typography>
            <BotResponseRenderer message={botPreviewMessage} />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                디버그
              </Typography>
              <DebugRow label="intent" value={result?.intent} />
              <DebugRow label="confidence" value={result?.confidence} />
              <DebugRow label="dryRunToken" value={result?.dryRunToken} />
              <DebugRow label="latencyMs" value={result?.latencyMs} />
              <DebugRow label="fastPathMatched" value={result ? String(result.fastPathMatched) : undefined} />
              <DebugBlock label="slots" value={result?.slots} />
              <DebugBlock label="rawLlmOutput" value={result?.rawLlmOutput} />
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const DebugRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right', wordBreak: 'break-word' }}>
      {value ?? '-'}
    </Typography>
  </Stack>
);

const DebugBlock = ({ label, value }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
      {label}
    </Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.25,
        bgcolor: '#101828',
        color: '#f9fafb',
        borderRadius: 1,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      {prettyJson(value)}
    </Box>
  </Box>
);

export default IntentBotTestPage;
